import * as React from "react";
import {Autocomplete, Stack, ToggleButton, ToggleButtonGroup} from "@mui/material";
import TextField from "@mui/material/TextField";
import {useTranslation} from "react-i18next";
import {MaileonTransactionType} from "crqlar-react-common/dist/models/marketing/maileonTransactionType.model";
import {LocalizedConfirmationTemplate} from "crqlar-react-common/dist/models/sector/sector.model";
import {getOrganizationLanguages} from "crqlar-react-common/dist/utils/useSupportedLanguages";
import {getIconForLanguage, getNameForLanguage} from "crqlar-react-common/dist/utils/translations.util";
import {useOrganization} from "crqlar-react-common/dist/state/auth.reducer";

interface LocalizedTransactionTypeFieldProps {
    value?: LocalizedConfirmationTemplate;
    onChange: (value: LocalizedConfirmationTemplate) => void;
    transactionTypes: MaileonTransactionType[];
}

const LocalizedTransactionTypeField: React.FC<LocalizedTransactionTypeFieldProps> = ({
    value,
    onChange,
    transactionTypes
}) => {
    const {t} = useTranslation();
    const organization = useOrganization();
    const supportedLanguages = getOrganizationLanguages(organization);

    const [selectedLanguage, setSelectedLanguage] = React.useState<string>(
        supportedLanguages[0] || 'en'
    );

    const handleLanguageChange = (
        event: React.MouseEvent<HTMLElement>,
        newLanguage: string | null
    ) => {
        if (newLanguage !== null) {
            setSelectedLanguage(newLanguage);
        }
    };

    const selectedValue = value?.[selectedLanguage];
    const selectedTransactionType = transactionTypes.find(tt => tt.id === selectedValue) || null;

    const handleTransactionTypeChange = (
        event: any,
        newValue: MaileonTransactionType | null
    ) => {
        const updatedValue = { ...value };
        if (newValue?.id) {
            updatedValue[selectedLanguage] = newValue.id;
        } else {
            delete updatedValue[selectedLanguage];
        }
        onChange(updatedValue);
    };

    return (
        <Stack
            spacing={1.5}
            sx={{mt: 1}}
        >
            <ToggleButtonGroup
                value={selectedLanguage}
                exclusive
                onChange={handleLanguageChange}
                size="small"
                sx={{ gap: 0.5 }}
            >
                {supportedLanguages.map((lang) => (
                    <ToggleButton
                        key={lang}
                        value={lang}
                        sx={{
                            px: 1,
                            py: 0.5,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5
                        }}
                    >
                        <img
                            alt={getNameForLanguage(lang)}
                            style={{ width: '16px', height: '16px' }}
                            src={getIconForLanguage(lang)}
                        />
                        <span style={{ fontSize: '0.875rem' }}>
                            {lang.toUpperCase()}
                        </span>
                    </ToggleButton>
                ))}
            </ToggleButtonGroup>

            <Autocomplete
                options={transactionTypes}
                getOptionLabel={(option: MaileonTransactionType) => option.name || t('Do not send')}
                value={selectedTransactionType}
                fullWidth
                sx={{
                    minWidth: '300px'
                }}
                onChange={handleTransactionTypeChange}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        fullWidth
                        size="small"
                        placeholder={t('Select template for {{language}}', {
                            language: getNameForLanguage(selectedLanguage)
                        })}
                    />
                )}
            />
        </Stack>
    );
};

export default LocalizedTransactionTypeField;
